import { api } from '../../services/apiClient';

export interface CalBlock {
  schedule_id: number; weekday: number; start_time: string; end_time: string;
  class_id: number; name: string; color: string | null; capacity: number | null;
  classroom: string | null; style: string | null; teacher: string | null;
  enrolled: number; image_url: string | null;
  // VIGENCIA del ciclo (como en Kydemy): fuera de este rango la clase NO existe ese día.
  // null = sin rango definido (alta manual antigua) → se considera siempre vigente.
  start_date?: string | null; end_date?: string | null;
  /** 'active' | 'archived' — solo viaja con ?estado=todas. */
  status?: string;
}

export interface ClassRow {
  class_id: number; name: string; color: string | null; capacity: number | null;
  status: string; level_code: string | null; style: string | null; teacher: string | null;
  schedule_blocks: number;
  start_date?: string | null; end_date?: string | null; expired?: number;
}

// ¿La clase de este bloque corre en la fecha dada? `date` en formato YYYY-MM-DD.
// El horario semanal (weekday+hora) NO basta: sin esto una clase del semestre
// pasado se repite para siempre (bug detectado el 29-jul-2026).
export function isActiveOn(b: { start_date?: string | null; end_date?: string | null }, date: string) {
  if (b.start_date && date < b.start_date) return false;
  if (b.end_date && date > b.end_date) return false;
  return true;
}
// YYYY-MM-DD en hora LOCAL (no usar toISOString: desplaza el día en México).
export function ymd(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
export const DAYS_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// Forma del backend VIVO (lealify-base /letsmove/classes/:id/roster): alumno = contacts.
/** Semáforo de cobro de un mes (pedido de Karla: ver de un golpe quién no ha pagado). */
export interface MonthPayState {
  month: string;                                       // 'YYYY-MM'
  status: 'paid' | 'partial' | 'due' | 'pending' | 'na';
  paid: number; expected: number;
  /** Motivo escrito del pago ('Cierre de ciclo jul-2026: 65% del mes'): explica un mes que no
   *  cuadra con la tarifa sin tener que reconstruirlo cruzando pagos contra precios viejos. */
  note?: string | null;
}
/** Quien pagó CLASE SUELTA / MUESTRA de esta clase para ese día sin estar inscrito (F3, Karla 30-sep):
 *  se le pasa lista aquí mismo sin inscribirlo — inscribirlo lo volvería alumno. */
export interface RosterVisitante {
  contact_id: number; first_name: string; last_name: string;
  has_photo: number; status: string | null;
  /** 1 = sigue siendo prospecto (no alumna de otra clase). */
  es_prospecto: number;
  /** sin_pago = ya tenía asistencia en esta lista sin pago de este día que la respalde. */
  visita: 'clase_suelta' | 'muestra' | 'sin_pago';
  pagado: number;
}
export interface RosterStudent {
  contact_id: number; first_name: string; last_name: string;
  has_photo: number; payment_ok: number | null; status: string | null;
  /** Mes anterior · actual · siguiente respecto a la fecha vista. */
  months?: MonthPayState[];
  effective_month?: number | null;
  /** Para el tooltip del renglón: plan, en qué otras clases está y cuánto debe en total. */
  plan_name?: string | null;
  clases?: { class_id: number; name: string; hora: string | null; dias: number[] }[];
  debe_total?: number;
  debe_meses?: string[];
}

export function getCalendar() { return api.get<{ data: CalBlock[] }>('/classes/calendar'); }
/** Con las ARCHIVADAS incluidas — solo para el filtro de estado de la lista/tarjetas. */
export function getCalendarTodas() { return api.get<{ data: CalBlock[] }>('/classes/calendar?estado=todas'); }

// ── Caché local del calendario (stale-while-revalidate) ──
// El horario semanal casi no cambia: las vistas pintan AL INSTANTE lo último
// conocido (localStorage) y refrescan por detrás. Evita el "se va construyendo
// por partes" del Home mientras corre la cascada sesión→permisos→fetch.
// v2 (29-jul-2026): el caché v1 no traía start_date/end_date; si se reusara, las
// clases vencidas seguirían pintándose hasta el primer refresh.
const CAL_CACHE_KEY = 'lm-calendar-v2';
export function readCalendarCache(): CalBlock[] | null {
  try {
    const arr = JSON.parse(localStorage.getItem(CAL_CACHE_KEY) || 'null');
    return Array.isArray(arr) && arr.length ? arr : null;
  } catch { return null; }
}
export async function getCalendarFresh(): Promise<CalBlock[]> {
  const r = await getCalendar();
  try { localStorage.setItem(CAL_CACHE_KEY, JSON.stringify(r.data)); } catch { /* sin espacio: no pasa nada */ }
  return r.data;
}
export function listClasses(opts?: { status?: string; vigencia?: 'vigentes' | 'vencidas' }) {
  const q = new URLSearchParams();
  if (opts?.status) q.set('status', opts.status);
  if (opts?.vigencia) q.set('vigencia', opts.vigencia);
  const qs = q.toString();
  return api.get<{ data: ClassRow[] }>(`/classes${qs ? `?${qs}` : ''}`);
}
export function getClass(id: number) { return api.get<{ data: any }>(`/classes/${id}`); }

export interface Occupancy {
  class_id: number; name: string; color: string | null;
  capacity: number | null; enrolled: number; teacher: string | null;
  avatars?: { account_id: number; firstname: string }[];
}
export function getOccupancy() { return api.get<{ data: Occupancy[] }>('/classes/occupancy'); }
export function getRoster(id: number, date: string) {
  return api.get<{ data: RosterStudent[]; visitantes?: RosterVisitante[]; date: string; months: string[] }>(`/classes/${id}/roster?date=${date}`);
}
/** Resultado de bajar a alguien de una clase: si quedó sin clases y qué adeudos abiertos tiene. */
export interface UnenrollInfo {
  classes_left: number; left_without_classes: boolean;
  open_debts: { debt_id: number; month: string | null; amount: string; comment: string | null }[];
  waitlist_id?: number | null;
}
/** Baja de la clase pero CONSERVA el lugar en la lista de espera (puede regresar). */
export function moveStudentToWaitlist(classId: number, contactId: number, note?: string) {
  return api.post<{ data: UnenrollInfo }>(`/classes/${classId}/students/${contactId}/waitlist`, { note });
}
/** Quita al alumno de esta clase (sigue en las demás). */
export function removeStudentFromClass(classId: number, contactId: number) {
  return api.del<{ data: UnenrollInfo }>(`/classes/${classId}/students/${contactId}`);
}
// El backend lee la clave `account_id` de cada record (legado) pero la guarda como contact_id:
// el valor que se manda ES el contact_id del alumno.
export function saveAttendance(id: number, date: string, records: { account_id: number; status: string }[]) {
  return api.post<{ ok: true; saved: number }>(`/classes/${id}/attendance`, { date, records });
}
export function hhmm(t: string) { return (t || '').slice(0, 5); }

// ===== Asistencia del MAESTRO (Karla 29-sep): vino / no vino / suplente → directo a la nómina =====
// Escribe la misma excepción que Finanzas → Nómina (letsmove_payroll_exceptions). Marca recepción;
// el maestro solo la ve (`can_edit=false`).
export type TeacherAttendanceStatus = 'came' | 'absent' | 'substitute';
export interface TeacherStatus {
  teacher: { id: number; name: string; has_photo?: boolean } | null;
  status: TeacherAttendanceStatus;
  substitute: { id: number; name: string } | null;
  note: string | null; marked_by: string | null; marked_at: string | null;
  has_session: boolean; can_edit: boolean;
  teachers: { id: number; name: string; has_rate: boolean }[];
}
export function getTeacherStatus(classId: number, date: string) {
  return api.get<{ data: TeacherStatus }>(`/classes/${classId}/teacher-status?date=${date}`);
}
export function setTeacherStatus(classId: number, body: { date: string; status: TeacherAttendanceStatus; substitute_user_id?: number; note?: string }) {
  return api.put<{ ok: true; warning: string | null }>(`/classes/${classId}/teacher-status`, body);
}

// ===== Alta / edición de clases (admin) =====
export interface CatalogItem { style_id?: number; classroom_id?: number; account_id?: number; name: string; location_id?: number | null; }
export interface ClassCatalogs { styles: CatalogItem[]; classrooms: CatalogItem[]; teachers: CatalogItem[]; }
export function getCatalogs() { return api.get<{ data: ClassCatalogs }>('/classes/catalogs'); }

export interface ScheduleBlockInput {
  weekday: number; start_time: string; end_time: string; classroom_id: number | null;
}
export interface ClassInput {
  name: string; description?: string | null; style_id?: number | null; level_code?: string | null;
  capacity?: number | null; teacher_leader_id?: number | null; teacher_follower_id?: number | null;
  /** Precio de clase suelta propio de la clase; null = regla general de trial_prices. */
  price?: number | null;
  classroom_id?: number | null; color?: string | null; schedule: ScheduleBlockInput[];
}
export function createClass(body: ClassInput) { return api.post<{ data: { class_id: number } }>('/classes', body); }
export function updateClass(id: number, body: ClassInput) { return api.put<{ data: { class_id: number } }>(`/classes/${id}`, body); }
// force=true borra también las inscripciones (2ª confirmación en el panel). La asistencia
// tomada sigue bloqueando el borrado en el backend: eso es historia, se archiva en su lugar.
export function deleteClass(id: number, force = false) { return api.del<{ data: { deleted: boolean; unenrolled: number } }>(`/classes/${id}${force ? '?force=1' : ''}`); }
export function setClassStatus(id: number, status: 'active' | 'archived') {
  return api.patch<{ data: { status: string } }>(`/classes/${id}/status`, { status });
}

// ===== Asistente «Clonar clases» al ciclo nuevo =====
// Réplica de «Clonar y Actualización de Clases» de Kydemy: la clase nueva se lleva
// alumnos, horario, profe y salón; la original se archiva como histórico.
export interface CloneCandidate {
  class_id: number; name: string; color: string | null; capacity: number | null;
  level_code: string | null; kydemy_id: number | null;
  start_date: string | null; end_date: string | null; expired: boolean;
  style: string | null; teacher: string | null;
  schedule_blocks: number; students: number; waitlist: number;
  clones: { class_id: number; start_date: string | null }[];
}
export interface CloneOptions {
  copy_students: boolean; copy_waitlist: boolean; archive_original: boolean;
  rename_original: boolean; level_up: boolean; generate_debt: boolean;
}
export interface ClonePreview {
  classes: number; enrollments: number; waitlist: number; schedule_blocks: number;
  skipped: { class_id: number; clone_id: number; name: string }[];
  debts: null | {
    month: string; due_date: string; students: number; total: number;
    without_plan: string[]; already_charged: string[];
  };
  detalle: { class_id: number; name: string; end_date: string | null; students: number; waitlist: number }[];
}
export interface CloneResult {
  ok: true; classes: number; enrollments: number; waitlist: number;
  debts: { count: number; total: number; month: string };
  skipped: { name: string }[]; created_class_ids: number[]; log: string;
}
export function getCloneCandidates() {
  return api.get<{ data: CloneCandidate[]; suggest: { start_date: string; end_date: string; days: number } | null }>(
    '/classes/clone/candidates');
}
export interface CloneBody extends Partial<CloneOptions> {
  class_ids: number[]; start_date: string; end_date: string;
  debt_month?: string; debt_due_day?: number; dry_run?: boolean;
}
export function cloneClasses(body: CloneBody) {
  return api.post<ClonePreview & Partial<CloneResult> & { dry_run?: boolean }>('/classes/clone', body);
}

// ===== Lista de espera / Prospectos por clase =====
export type WaitlistStatus = 'waiting' | 'contacted' | 'enrolled' | 'discarded';
export interface WaitlistRow {
  id: number; class_id: number; contact_id: number; status: WaitlistStatus;
  note: string | null; start_date: string | null; source: string; from_kydemy: number;
  first_name: string; last_name: string | null; phone: string | null; has_photo: number;
  class_name: string; class_color: string | null; capacity: number | null; enrolled: number;
}
export function getWaitlist(status?: WaitlistStatus) {
  return api.get<{ data: WaitlistRow[] }>(`/waitlist${status ? `?status=${status}` : ''}`);
}
export function getClassWaitlist(classId: number) {
  return api.get<{ data: WaitlistRow[] }>(`/waitlist/class/${classId}`);
}
export function addToWaitlist(classId: number, body: { contact_id?: number; firstname?: string; lastname?: string; phone?: string; note?: string }) {
  return api.post<{ data: { id: number; contact_id: number } }>(`/waitlist/class/${classId}`, body);
}
export function updateWaitlist(id: number, patch: { status?: WaitlistStatus; note?: string }) {
  return api.patch<{ ok: true }>(`/waitlist/${id}`, patch);
}
export function enrollFromWaitlist(id: number) {
  // {} obligatorio: el proxy LiteSpeed rechaza POST con body vacío (gotcha conocido)
  return api.post<{ ok: true }>(`/waitlist/${id}/enroll`, {});
}
export function removeFromWaitlist(id: number) {
  return api.del<{ ok: true }>(`/waitlist/${id}`);
}
